import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { useSEO } from "@/lib/useSEO";

const LAST_UPDATED = "9 August 2026";

const H2 = ({ children }) => (
  <h2 className="font-serif-display italic text-[#F5F4F0] text-2xl md:text-3xl font-light tracking-tight mt-14 mb-4 leading-tight">
    {children}
  </h2>
);
const H3 = ({ children }) => (
  <h3 className="text-[#C9A876] text-[10px] tracking-[0.32em] uppercase font-body mt-8 mb-3">
    {children}
  </h3>
);
const P = ({ children }) => (
  <p className="text-[#D6D1CA]/80 text-sm md:text-[15px] leading-[1.75] font-body mb-4">
    {children}
  </p>
);
const UL = ({ children }) => (
  <ul className="list-disc list-outside pl-5 space-y-2 text-[#D6D1CA]/80 text-sm md:text-[15px] leading-[1.75] font-body mb-4">
    {children}
  </ul>
);

export default function Privacy() {
  useSEO({
    title: "Privacy Policy · My Floor Studio",
    description:
      "How My Floor Studio (ABN 87 651 648 029) collects, uses, stores and protects your personal information under the Australian Privacy Act 1988 and the Australian Privacy Principles.",
    path: "/privacy",
    keywords: "my floor studio privacy policy, Australian Privacy Act, APP compliance, personal information handling",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://myfloorstudio.com.au/" },
          { "@type": "ListItem", position: 2, name: "Privacy Policy", item: "https://myfloorstudio.com.au/privacy" },
        ],
      },
    ],
  });
  return (
    <>
      <Nav />
      <main className="bg-[#121315] pt-32 pb-24" data-testid="privacy-page">
        <div className="mx-auto max-w-3xl px-6 md:px-8">
          <p className="text-[#C9A876] text-[10px] tracking-[0.4em] uppercase mb-4">Legal</p>
          <h1 className="font-serif-display italic text-[#F5F4F0] text-4xl md:text-5xl font-light leading-none tracking-tight">
            Privacy Policy
          </h1>
          <p className="text-[#D6D1CA]/60 text-xs tracking-[0.28em] uppercase font-body mt-6">
            Last updated · {LAST_UPDATED}
          </p>

          <div className="mt-12">
            <P>
              My Floor Studio (<strong className="text-[#F5F4F0]">ABN 87 651 648 029</strong>){" "}
              (<em>“we”, “our”, “us”</em>) is committed to protecting your privacy. This policy
              explains how we collect, use, hold, disclose and protect your personal information
              in accordance with the{" "}
              <em>Privacy Act 1988 (Cth)</em> and the Australian Privacy Principles (APPs).
            </P>
            <P>
              This policy applies to information collected through our website{" "}
              <a href="https://myfloorstudio.com.au" className="text-[#C9A876] link-underline">myfloorstudio.com.au</a>,
              our Drummoyne showroom, and any dealings we have with you by phone, email, or in
              person.
            </P>

            <H2>1. What information we collect</H2>
            <P>
              We only collect personal information that is reasonably necessary to deliver our
              services. Depending on how you interact with us, we may collect:
            </P>
            <UL>
              <li><strong className="text-[#F5F4F0]">Identity &amp; contact details</strong> — name, email, phone, postal or delivery address.</li>
              <li><strong className="text-[#F5F4F0]">Enquiry details</strong> — subject and content of messages, sample kit requests, appointment preferences and any project notes you share.</li>
              <li><strong className="text-[#F5F4F0]">Trade / professional details</strong> — for architects, designers and builders, we may collect your firm name, ABN, licence number and business address.</li>
              <li><strong className="text-[#F5F4F0]">Transactional information</strong> — records of samples dispatched, quotes issued and orders fulfilled.</li>
              <li><strong className="text-[#F5F4F0]">Website usage data</strong> — anonymous technical information such as browser type, device type, referring page and the pages you view. This is collected in aggregate and does not identify you personally.</li>
            </UL>

            <H2>2. How we collect it</H2>
            <UL>
              <li>Directly from you when you complete our booking, sample or contact forms.</li>
              <li>Directly from you when you email, call or visit the studio.</li>
              <li>From publicly available sources (e.g. an architectural practice’s website) if you contact us in a trade capacity.</li>
            </UL>

            <H2>3. How we use your information</H2>
            <P>We use your personal information to:</P>
            <UL>
              <li>Respond to enquiries and process sample-kit or appointment requests.</li>
              <li>Prepare quotes, invoices and delivery arrangements.</li>
              <li>Send you material updates, sample tracking, and (where you have opted in) occasional newsletters about new ranges and studio events.</li>
              <li>Improve the quality of our website, showroom and service.</li>
              <li>Meet our legal, accounting and tax obligations.</li>
            </UL>
            <P>
              We do <strong className="text-[#F5F4F0]">not</strong> sell your personal information
              to third parties and we do not use it for any purpose that is not reasonably
              connected to why it was collected.
            </P>

            <H2>4. Who we may share it with</H2>
            <P>We may disclose your information, on a need-to-know basis, to:</P>
            <UL>
              <li>Our courier and logistics partners in order to deliver samples or product.</li>
              <li>Our accounting, IT hosting and email service providers under confidentiality obligations.</li>
              <li>Government or regulatory authorities where we are required by law to do so.</li>
            </UL>
            <P>
              Our email delivery is handled by Resend Inc. (transactional emails) and our
              database is hosted on MongoDB Atlas. Both operate under strong data-protection
              standards and process data only on our instructions. Some of these providers may
              store data outside Australia, including in the United States and the European Union.
              We take reasonable steps to ensure any overseas recipient complies with the APPs.
            </P>

            <H2>5. Marketing communications</H2>
            <P>
              We will only send you marketing communications where you have opted in or where
              it is reasonable to expect them given an existing customer relationship. Every
              marketing email includes an unsubscribe link, and you may withdraw consent at any
              time by emailing{" "}
              <a href="mailto:info@myfloorstudio.com.au" className="text-[#C9A876] link-underline">info@myfloorstudio.com.au</a>.
            </P>

            <H2>6. How we store &amp; protect your information</H2>
            <P>
              Your information is held in secure cloud infrastructure with encryption at rest
              and in transit. Access is restricted to authorised personnel using
              credential-based authentication. We retain personal information only for as long
              as necessary to fulfil the purposes for which it was collected or as required by
              law (typically seven years for tax and accounting records).
            </P>

            <H2>7. Cookies &amp; tracking</H2>
            <P>
              Our website does not use tracking cookies, third-party analytics or advertising
              pixels at this time. If we introduce any tracking technology in future, we will
              update this policy and, where required, seek your consent through a cookie
              banner.
            </P>

            <H2>8. Your rights (access, correction &amp; deletion)</H2>
            <P>
              Under the APPs you have the right to:
            </P>
            <UL>
              <li>Request access to the personal information we hold about you.</li>
              <li>Request that we correct information that is inaccurate, out of date or incomplete.</li>
              <li>Request that we delete your information, subject to any legal obligation we may have to retain it.</li>
              <li>Withdraw consent to marketing communications at any time.</li>
            </UL>
            <P>
              To exercise any of these rights, email{" "}
              <a href="mailto:info@myfloorstudio.com.au" className="text-[#C9A876] link-underline">info@myfloorstudio.com.au</a>{" "}
              or write to us at the address below. We will respond within 30 days.
            </P>

            <H2>9. Data breaches</H2>
            <P>
              We maintain incident-response procedures aligned with the Notifiable Data
              Breaches scheme. In the unlikely event of a data breach that is likely to result
              in serious harm, we will notify affected individuals and the Office of the
              Australian Information Commissioner (OAIC) without undue delay.
            </P>

            <H2>10. Complaints</H2>
            <P>
              If you believe we have breached your privacy, contact us in the first instance so
              we can investigate and respond. If you are not satisfied with our response you
              may lodge a complaint with the OAIC at{" "}
              <a href="https://www.oaic.gov.au" target="_blank" rel="noreferrer" className="text-[#C9A876] link-underline">oaic.gov.au</a>{" "}
              or by phone on 1300 363 992.
            </P>

            <H2>11. Changes to this policy</H2>
            <P>
              We may update this policy from time to time. Any material change will be
              signposted on this page with a revised “Last updated” date.
            </P>

            <H2>12. Contact us</H2>
            <div className="mt-2 border border-[#8C7355]/20 p-6 bg-[#0f1012]">
              <H3>Privacy Officer · My Floor Studio</H3>
              <P>184 Victoria Road, Drummoyne NSW 2047</P>
              <P>
                <a href="tel:+61272467899" className="text-[#C9A876] link-underline">02 7246 7899</a>{" "}
                &nbsp;·&nbsp;{" "}
                <a href="mailto:info@myfloorstudio.com.au" className="text-[#C9A876] link-underline">info@myfloorstudio.com.au</a>
              </P>
              <P>ABN 87 651 648 029</P>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
